"use client"

import { useState, useEffect, useCallback } from 'react'
import { useStore, DEFAULT_LOCK_PIN } from '@/lib/store'
import { cn } from '@/lib/utils'
import { Lock, Delete, ShieldCheck, KeyRound, AlertCircle } from 'lucide-react'
import { BRAND } from '@/lib/brand'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { useT } from '@/lib/i18n'
import { rich } from '@/lib/i18n/rich'

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del']

/** Mounted once per layout; renders nothing until it is needed. */
export function PinLock() {
  return (
    <>
      <PinSetupDialog />
      <LockScreen />
    </>
  )
}

/**
 * „Choose a PIN first". Opened by `lock()` when the shop never chose one, so
 * the screen does not lock behind a PIN everybody knows, and by the kiosk card
 * for the same reason (leaving the kiosk asks for this PIN).
 */
function PinSetupDialog() {
  const t = useT()
  const mode = useStore((s) => s.pinSetup)
  const close = useStore((s) => s.closePinSetup)
  const setPin = useStore((s) => s.setPin)
  const lock = useStore((s) => s.lock)
  const [pin, setPinInput] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => { if (mode) { setPinInput(''); setAgain(''); setError('') } }, [mode])

  const save = async () => {
    setError('')
    if (!/^\d{4,6}$/.test(pin)) { setError(t('lock.pinDigits')); return }
    if (pin === DEFAULT_LOCK_PIN) { setError(t('lock.pinKnown', { pin: DEFAULT_LOCK_PIN })); return }
    if (pin !== again) { setError(t('lock.pinMismatch')); return }
    setSaving(true)
    const ok = await setPin(pin)
    setSaving(false)
    if (!ok) return
    const thenLock = mode === 'lock'
    close()
    if (thenLock) lock()
    else toast.success(t('lock.pinSet'))
  }

  const field = 'h-11 pl-9 rounded-xl font-mono tracking-[0.35em] text-lg'

  return (
    <Dialog open={mode !== null} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-sm rounded-[24px] p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-black">{t('lock.setupTitle')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3.5 py-2">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {rich(t('lock.setupBody'), { pin: <span className="font-mono font-bold text-foreground">{DEFAULT_LOCK_PIN}</span> })}
          </p>
          {[
            { label: t('lock.newPin'), val: pin, set: setPinInput },
            { label: t('lock.repeat'), val: again, set: setAgain },
          ].map(({ label, val, set }) => (
            <div key={label} className="flex flex-col gap-1.5">
              <label className="text-[12px] font-bold text-muted-foreground uppercase tracking-wide pl-1">{label}</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input
                  type="password" inputMode="numeric" maxLength={6}
                  // an in-app PIN, not an account password — keep it out of
                  // the browser's save-password prompt
                  autoComplete="new-password" name="no-autofill-lock-pin"
                  className={field} placeholder="••••" value={val}
                  onChange={(e) => set(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={(e) => { if (e.key === 'Enter') void save() }}
                />
              </div>
            </div>
          ))}
          {error && (
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-xs font-medium bg-red-50 dark:bg-red-500/10 rounded-xl px-3 py-2 border border-red-200 dark:border-red-500/25">
              <AlertCircle className="size-3.5 shrink-0" />
              {error}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" className="rounded-xl h-11 px-5 font-bold text-muted-foreground" onClick={close} disabled={saving}>{t('common.cancel')}</Button>
          <Button onClick={() => void save()} disabled={saving || !pin || !again} className="rounded-xl h-11 px-6 font-black">
            {mode === 'lock' ? t('lock.setAndLock') : t('lock.set')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function LockScreen() {
  const t = useT()
  const { isLocked, unlock, settings } = useStore()
  const [input, setInput] = useState('')
  const [shake, setShake] = useState(false)
  const [success, setSuccess] = useState(false)

  const handleKey = useCallback((key: string) => {
    if (key === 'del') {
      setInput(p => p.slice(0, -1))
      return
    }
    if (input.length >= 6) return
    const next = input + key
    setInput(next)

    if (next.length >= 4) {
      const ok = unlock(next)
      if (ok) {
        setSuccess(true)
        setTimeout(() => { setSuccess(false); setInput('') }, 400)
      } else if (next.length === 6 || next.length === 4) {
        setShake(true)
        setTimeout(() => { setShake(false); setInput('') }, 600)
      }
    }
  }, [input, unlock])

  // Keyboard support
  useEffect(() => {
    if (!isLocked) return
    const handler = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') handleKey(e.key)
      if (e.key === 'Backspace') handleKey('del')
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isLocked, handleKey])

  if (!isLocked) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 dark:from-background dark:via-background dark:to-background">
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 size-96 rounded-full bg-primary/8 blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 size-80 rounded-full bg-indigo-400/10 blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none" />

      <div className="relative w-full max-w-xs mx-4">
        {/* Card */}
        <div className="bg-card/90 dark:bg-white/[0.04] backdrop-blur-xl rounded-3xl shadow-2xl shadow-primary/10 border border-white/60 dark:border-white/10 px-8 py-10 flex flex-col items-center gap-7">

          {/* Icon */}
          <div className={cn(
            'size-16 rounded-2xl flex items-center justify-center transition-all duration-300',
            success
              ? 'bg-gradient-to-br from-emerald-400 to-teal-500 shadow-lg shadow-emerald-500/30'
              : 'bg-gradient-to-br from-primary to-indigo-700 shadow-lg shadow-primary/30'
          )}>
            {success
              ? <ShieldCheck className="size-8 text-white" />
              : <Lock className="size-8 text-white" />
            }
          </div>

          {/* Title */}
          <div className="text-center">
            <h2 className="text-lg font-bold text-foreground">
              {settings.companyName || BRAND}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">{t('lock.enterPin')}</p>
          </div>

          {/* Dots */}
          <div className={cn('flex items-center gap-3 transition-all', shake && 'animate-shake')}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  'size-3.5 rounded-full border-2 transition-all duration-200',
                  i < input.length
                    ? success
                      ? 'bg-emerald-500 border-emerald-500 scale-110'
                      : 'bg-primary border-primary scale-110'
                    : shake
                      ? 'bg-red-300 border-red-400'
                      : 'bg-transparent border-border'
                )}
              />
            ))}
          </div>

          {/* Numpad */}
          <div className="grid grid-cols-3 gap-3 w-full">
            {KEYS.map((key, i) => {
              if (key === '') return <div key={i} />
              const isDel = key === 'del'
              return (
                <button
                  key={i}
                  onClick={() => handleKey(key)}
                  className={cn(
                    'h-14 rounded-2xl text-base font-semibold transition-all duration-150 active:scale-95 select-none',
                    isDel
                      ? 'bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive flex items-center justify-center'
                      : 'bg-accent hover:bg-primary hover:text-white hover:shadow-md hover:shadow-primary/30 text-foreground'
                  )}
                >
                  {isDel ? <Delete className="size-4.5" /> : key}
                </button>
              )
            })}
          </div>

          {shake && (
            <p className="text-xs text-red-500 font-medium animate-fade-in">{t('lock.wrongPin')}</p>
          )}
        </div>
      </div>
    </div>
  )
}
